import React, { useEffect, useState } from 'react'
import moment from 'moment'

import TimeframeButton from './TimeframeButton'
import { Select } from '../../../module'
import { getNextAvailableTimeframeOption } from '../../../services/TimeframeService'
import styles from '../_timeframe-filter.module.scss'
import {
  type CalloutType,
  type CustomAggregationsProps,
  type TimeframeProps,
} from '../TimeframesType'

const timeframes: TimeframeProps[] = [
  {
    id: 1,
    display: '1Y',
    timeValue: 'year',
    type: 'quarterly',
  },
  {
    id: 2,
    display: 'Q1',
    quarter: 1,
    timeValue: 'quarter',
    type: 'quarterly',
  },
  {
    id: 3,
    display: 'Q2',
    quarter: 2,
    timeValue: 'quarter',
    type: 'quarterly',
  },
  {
    id: 4,
    display: 'Q3',
    quarter: 3,
    timeValue: 'quarter',
    type: 'quarterly',
  },
  {
    id: 5,
    display: 'Q4',
    quarter: 4,
    timeValue: 'quarter',
    type: 'quarterly',
  },
]

const currentYear = new Date().getFullYear()
const years: {
  id: string
  value: string
}[] = []
for (let i = currentYear; i >= 2013; i--) {
  years.push({ id: `year-${i}`, value: i.toString() })
}

type QuarterlyTimeframeProps = {
  callout: (
    type: CalloutType,
    startDate: moment.Moment,
    endDate: moment.Moment,
    timeframe: TimeframeProps,
  ) => void
  closeMenu?: () => void
  timeframe: TimeframeProps
  useTimeframeAggregations?: boolean
  customAggregations?: CustomAggregationsProps
  disabledTimeframeOptions?: string[]
}

export const QuarterlyTimeframe = ({
  callout,
  closeMenu,
  timeframe,
  useTimeframeAggregations = false,
  customAggregations,
  disabledTimeframeOptions,
}: QuarterlyTimeframeProps) => {
  const defaultYear = timeframe.year ?? currentYear

  const [year, setYear] = useState({
    value: defaultYear,
  })

  useEffect(() => {
    // This is to display current year on Reset global filter
    setYear({ value: defaultYear })
  }, [defaultYear])

  useEffect(() => {
    // If currently selected timeframe option is disabled for visited page
    // then switch to next available timeframe option.
    if (
      disabledTimeframeOptions?.includes(
        timeframe.quarter ? timeframe?.display?.split(' ')[1] : '1Y',
      )
    ) {
      getNextAvailableTimeframeOption({
        timeframes,
        type: timeframe.type,
        disabledTimeframeOptions,
        //@ts-expect-error - This value comes from a .js file and is not typed
        year: year.value,
        callout,
        useTimeframeAggregations,
        customAggregations,
        getAggregation,
      })
    }
  }, [
    disabledTimeframeOptions,
    callout,
    customAggregations,
    timeframe.type,
    timeframe.display,
    timeframe.quarter,
    useTimeframeAggregations,
    year.value,
  ])

  const updateTimeframe = (e: TimeframeProps) => {
    let startDate, endDate
    if (e.quarter) {
      startDate = moment(moment().year(Number(year.value)))
        .quarter(e.quarter)
        .startOf('quarter')

      endDate = moment(moment().year(Number(year.value)))
        .quarter(e.quarter)
        .endOf('quarter')
    } else {
      startDate = moment().year(Number(year.value)).startOf('year')
      endDate = moment().year(Number(year.value)).endOf('year')
    }

    const newTimeframe: TimeframeProps = {
      type: 'quarterly',
      display: `${year.value}${e.quarter ? ` ${e.display}` : ''}`,
      year: year.value.toString(),
      quarter: e.quarter,
      timeValue: e.timeValue,
    }

    if (useTimeframeAggregations) {
      newTimeframe.aggregation = getAggregation(
        newTimeframe,
        customAggregations,
      )
    }

    callout('UPDATE_RANGE', startDate, endDate, newTimeframe)
    closeMenu && closeMenu()
  }

  const updateYear = (option: { value: number | string }) => {
    if (option) {
      setYear(option)
      const startDate = moment().year(Number(option.value)).startOf('year'),
        endDate = moment().year(Number(option.value)).endOf('year')

      const newTimeframe: TimeframeProps = {
        type: 'quarterly',
        display: option.value.toString(),
        year: option.value.toString(),
        timeValue: 'year',
      }

      if (useTimeframeAggregations) {
        newTimeframe.aggregation = getAggregation(
          newTimeframe,
          customAggregations,
        )
      }

      callout('UPDATE_RANGE', startDate, endDate, newTimeframe)
      closeMenu?.()
    }
  }

  return (
    <div className={styles.timeframeFilter}>
      <Select
        options={years}
        selectedItem={year}
        optionKeyName='value'
        labelKeyName='value'
        onChange={(option) => updateYear(option)}
        disabled={
          (disabledTimeframeOptions?.includes('1Y') ||
            disabledTimeframeOptions?.includes('Year')) ??
          false
        }
        noBorder
        menuWidth={100}
      />
      {timeframes.map((t) => {
        let disabled =
          (disabledTimeframeOptions?.includes(t.display) || t.disabled) ?? false
        if (
          t.quarter &&
          moment(moment().year(Number(year.value)))
            .quarter(t.quarter)
            .startOf('quarter')
            .isAfter(new Date())
        ) {
          disabled = true
        }
        return (
          <TimeframeButton
            el={t}
            updateTimeframe={updateTimeframe}
            timeframe={timeframe}
            disabled={disabled}
            key={t.id}
            customDisplay={`${year.value}${t.quarter ? ` ${t.display}` : ''}`}
          />
        )
      })}
    </div>
  )
}

function getAggregation(
  timeframe: TimeframeProps,
  customAggregations?: CustomAggregationsProps,
) {
  const validAggregations = getValidAggregationsForQuarterlyTimeframe(
    timeframe,
    customAggregations,
  )
  return validAggregations?.[0]
}

export function getValidAggregationsForQuarterlyTimeframe(
  timeframe: TimeframeProps,
  customAggregations?: CustomAggregationsProps,
) {
  // NOTE: The first item in the array will be the default for each timeValue
  if (timeframe.timeValue === 'quarter') {
    return customAggregations
      ? customAggregations.quarter
      : ['month', 'week', 'day']
  }

  return customAggregations ? customAggregations.year : ['month', 'week', 'day']
}
